import React from 'react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { NumberSignEnum } from '../../../lib/enums';
import { BaseSymbolData, Week52HighLowResponse } from '../../../lib/models/fundamental/types';
import TradePriceCache from '../../../lib/store-util/TradePriceCache';
import { formatCurrency, getLimitedText } from '../../../lib/util/DataHelpers';
import { calculatePriceMovement, roundToCurrencyFormat } from '../../../lib/util/lastPriceChangeHelpers';
import { getSymbolFirstPart } from '../../../lib/util/MarketDataHelpers';
import { INVALID_VALUE, PriceMovement } from '../../../lib/util/types';

import { ChartLine, CompanyLogo } from '../../../components';
import { useIsMobile } from '../../../hooks';
import { UI_ROUTES } from '../../../util/routes';

import styles from './PopularGroupTable.module.scss';

type Props = {
    item: BaseSymbolData | Week52HighLowResponse;
    isWeek52Item?: boolean;
}

let priceMovement: PriceMovement;

const PopularGroupTableItem = ({ item, isWeek52Item }: Props) => {
  const isMobile = useIsMobile();
  const history = useHistory();
  const {
    direction, change, changePercentWithSign, currentPrice,
  } = TradePriceCache.use(getSymbolFirstPart(item.symbol), 'PopularGroupTableItem');
  const nameLimit = isMobile ? 16 : 20;

  priceMovement = calculatePriceMovement(currentPrice, item.symbol);

  function handleOnClick() {
    history.push(`${UI_ROUTES.symbol}/${item.symbol}`);
  }

  const getChangePercentageByItemType = ():JSX.Element => {
    const content: JSX.Element[] = [
      <div
        key={item.symbol}
        className={classNames(styles.columnPercentChange, { [styles.columnPercentChangeMob]: isMobile })}
      >
        {changePercentWithSign ? (
          <span className={
            direction === NumberSignEnum.Negative ? styles.priceTextMinus : styles.priceTextPlus
          }
          >
            {changePercentWithSign}
          </span>
        ) : (
          INVALID_VALUE
        )}
      </div>,
    ];

    if (isWeek52Item) {
      const item52Week = item as Week52HighLowResponse;
      content.push(
        <div className={classNames(styles.columnPercentChange, { [styles.columnPercentChangeMob]: isMobile })}>
          <span className={item52Week.changePercent > 0 ? styles.priceTextPlus : styles.priceTextMinus}>
            {item52Week.changePercent > 0 && NumberSignEnum.Positive}
            {item52Week.changePercent.toFixed(2)}
            %
          </span>
        </div>,
      );
    }

    return <>{content}</>;
  };

  return (
    <tr className={classNames(styles.wrapperRow, { [styles.row]: true })} onClick={handleOnClick}>
      {
            isMobile ? (
              <>
                <td
                  className={
                      classNames(
                        styles.columnSymbolName,
                        styles.columnSymbolNameWidthMobile,
                      )
                    }
                >
                  <div className={styles.wrapperContent}>
                    <div className={styles.logo}>
                      <CompanyLogo url={item.url || ''} logo={item.logoBase64 || ''} className={styles.logo} symbol={item.symbol} />
                    </div>
                    <div className={styles.companyInfo}>
                      <span className={styles.symbol}>{item.symbol}</span>
                      <span className={styles.companyName}>
                        {item.companyName && item.companyName.length > 12
                          ? `${item.companyName.slice(0, 12)}...`
                          : item.companyName}
                      </span>
                    </div>
                  </div>
                </td>
                {isWeek52Item ? (
                  <td>
                    <div>
                      {formatCurrency(currentPrice)}
                    </div>
                    <div className={styles.emptyRow} />
                  </td>
                ) : (
                  <td className={styles.columnChartMobile}><ChartLine symbol={item.symbol} /></td>
                )}
                <td className={styles.columnLastPriceMobile}>
                  {!isWeek52Item && (
                    <div className={styles.currentPrice}>
                      {formatCurrency(roundToCurrencyFormat(currentPrice || 0))}
                    </div>
                  )}
                  {getChangePercentageByItemType()}
                </td>
              </>
            ) : (
              <>
                <td className={styles.columnSymbolName}>
                  <div className={isWeek52Item ? styles.wrapperContent52Week : styles.wrapperContent}>
                    <div className={styles.logoWrapper}>
                      <CompanyLogo url={item.url || ''} logo={item.logoBase64 || ''} symbol={item.symbol} />
                    </div>
                    <div className={styles.companyInfo}>
                      <span className={styles.symbol}>{item.symbol}</span>
                      <span className={styles.companyName}>
                        {isWeek52Item ? getLimitedText(item.companyName, nameLimit) : item.companyName}
                      </span>
                    </div>
                  </div>
                </td>
                <td className={styles.columnChart}><ChartLine symbol={item.symbol} /></td>
                <td className={styles.columnLastPrice}>
                  <span className={classNames(styles.currentPrice, {
                    [styles.noPriceChange]: priceMovement === 'equal',
                    [styles.negativeChange]: priceMovement === 'down',
                    [styles.positiveChange]: priceMovement === 'up',
                  })}
                  >
                    {formatCurrency(currentPrice)}
                  </span>
                </td>
                <td className={styles.columnChange}>
                  {change
                    ? (
                      <span className={direction === '-' ? styles.priceTextMinus : styles.priceTextPlus}>
                        {isWeek52Item ? changePercentWithSign : formatCurrency(change)}
                      </span>
                    )
                    : INVALID_VALUE }
                </td>
                <td className={styles.columnPercentChange}>
                  {changePercentWithSign
                    ? (
                      <span className={direction === '-' ? styles.priceTextMinus : styles.priceTextPlus}>
                        {isWeek52Item ? `${direction === '-' ? '' : '+'}${(item as Week52HighLowResponse).changePercent.toFixed(2)}%` : changePercentWithSign}
                      </span>
                    )
                    : INVALID_VALUE}
                </td>
              </>
            )
        }
    </tr>
  );
};

export default PopularGroupTableItem;
